import React, { useEffect, useState, useMemo } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import MobileActionMenu from "../../components/MobileActionMenu";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import { toast } from "react-toastify";

import ServiceModal from "../../components/ServiceModal";
import { listServicos, deleteServico } from "../../services/servicosService";
import { useHistory } from "react-router-dom";
import { usePlanPermissions } from "../../context/PlanPermissionsContext";

const TEMPO_FILTERS = [
  { label: "Todos", value: "" },
  { label: "Com duração", value: "with" },
  { label: "Sem duração", value: "without" },
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: "transparent",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    borderBottom: "1px solid #e0e0e0",
    flexWrap: "wrap",
    gap: 12,
    [theme.breakpoints.down("sm")]: { padding: "12px 16px", flexDirection: "column", alignItems: "stretch" },
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  headerTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#1a1a1a",
    [theme.breakpoints.down("sm")]: { fontSize: "1.2rem" },
  },
  headerSubtitle: {
    fontSize: "0.875rem",
    color: "#666",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: { flexDirection: "column", alignItems: "stretch" },
  },
  searchField: {
    backgroundColor: "#fff",
    borderRadius: 8,
    minWidth: 220,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#1976d2" },
    },
    [theme.breakpoints.down("sm")]: { minWidth: "unset", width: "100%" },
  },
  filterSelect: {
    minWidth: 150,
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-root": { borderRadius: 8 },
    [theme.breakpoints.down("sm")]: { minWidth: "unset", width: "100%" },
  },
  addButton: {
    borderRadius: 8,
    padding: "6px 20px",
    textTransform: "none",
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: { width: "100%" },
  },
  content: {
    padding: "16px 24px 24px",
    [theme.breakpoints.down("sm")]: { padding: "12px 12px 16px" },
  },
  tableWrapper: {
    backgroundColor: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    [theme.breakpoints.down("sm")]: { overflowX: "auto" },
  },
  tableHead: {
    backgroundColor: "var(--primary-color, #1e293b)",
    "& th": {
      color: "#fff",
      fontWeight: 600,
      fontSize: "0.8rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      borderBottom: "none",
      padding: "14px 16px",
    },
  },
  tableBody: {
    "& td": {
      padding: "12px 16px",
      fontSize: "0.875rem",
      color: "#334155",
      borderBottom: "1px solid #f1f5f9",
    },
    "& tr:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderTop: "1px solid #f1f5f9",
    backgroundColor: "#fff",
    borderRadius: "0 0 12px 12px",
    flexWrap: "wrap",
    gap: 8,
    [theme.breakpoints.down("sm")]: { padding: "8px 12px" },
  },
  actionBtn: {
    minWidth: "auto",
    padding: "4px 8px",
    borderRadius: 6,
    fontWeight: 600,
    fontSize: "0.8rem",
    textTransform: "none",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    color: "#999",
  },
}));

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value) || 0);

const ServicosPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const primaryColor = theme.palette.primary.main || "#3b82f6";

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [tempoFilter, setTempoFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);

  const [tablePage, setTablePage] = useState(0);
  const rowsPerPage = 10;

  const { servicos, loading: planLoading } = usePlanPermissions();
  const history = useHistory();

  useEffect(() => {
    if (!planLoading && !servicos) {
      toast.warn("Seu plano não inclui este recurso.");
      history.push("/");
    }
  }, [planLoading, servicos]);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const { data } = await listServicos();
      const list = Array.isArray(data) ? data : (data?.servicos || data?.services || []);
      setServices(list);
    } catch (error) {
      toast.error("Não foi possível carregar os serviços");
    } finally {
      setLoading(false);
    }
  };

  const filteredServices = useMemo(() => {
    const term = search.trim().toLowerCase();
    return services.filter((s) => {
      const matchText =
        !term ||
        s.nome?.toLowerCase().includes(term) ||
        s.descricao?.toLowerCase().includes(term) ||
        String(s.id).includes(term);
      const matchTempo =
        tempoFilter === "" ||
        (tempoFilter === "with" && s.tempoAtendimento) ||
        (tempoFilter === "without" && !s.tempoAtendimento);
      return matchText && matchTempo;
    });
  }, [services, search, tempoFilter]);

  const paginatedServices = filteredServices.slice(tablePage * rowsPerPage, (tablePage + 1) * rowsPerPage);
  const totalPages = Math.ceil(filteredServices.length / rowsPerPage);

  const handleOpenModal = (service = null) => {
    setSelectedService(service);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedService(null);
    setModalOpen(false);
  };

  const handleModalSuccess = () => {
    handleCloseModal();
    fetchServices();
  };

  const handleDelete = (service) => {
    setServiceToDelete(service);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!serviceToDelete) return;
    try {
      await deleteServico(serviceToDelete.id);
      toast.success("Serviço removido com sucesso!");
      fetchServices();
    } catch {
      toast.error("Não foi possível remover o serviço");
    } finally {
      setDeleteDialogOpen(false);
      setServiceToDelete(null);
    }
  };

  return (
    <Box className={classes.root} style={{ "--primary-color": primaryColor }}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Box>
            <Typography className={classes.headerTitle}>Serviços</Typography>
            <Typography className={classes.headerSubtitle}>
              {filteredServices.length} {filteredServices.length === 1 ? "serviço encontrado" : "serviços encontrados"}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.headerRight}>
          <FormControl variant="outlined" size="small" className={classes.filterSelect}>
            <InputLabel>Duração</InputLabel>
            <Select value={tempoFilter} onChange={(e) => { setTempoFilter(e.target.value); setTablePage(0); }} label="Duração">
              {TEMPO_FILTERS.map((f) => (
                <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            placeholder="Buscar serviço..."
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setTablePage(0); }}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#999" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
            className={classes.addButton}
          >
            Novo Serviço
          </Button>
        </Box>
      </Box>

      {/* Conteúdo */}
      <Box className={classes.content}>
        <Box className={classes.tableWrapper}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell align="center">Duração</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>
              {loading && services.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box className={classes.emptyState}><CircularProgress size={32} /></Box>
                  </TableCell>
                </TableRow>
              ) : paginatedServices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box className={classes.emptyState}>
                      <MonetizationOnIcon style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }} />
                      <Typography>Nenhum serviço encontrado</Typography>
                      <Typography variant="body2" style={{ marginTop: 4 }}>
                        {search || tempoFilter
                          ? "Tente ajustar os filtros"
                          : "Clique em \"Novo Serviço\" para começar"}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedServices.map((service) => (
                  <TableRow key={service.id}>
                    <TableCell style={{ color: "#94a3b8", fontWeight: 500 }}>{service.id}</TableCell>
                    <TableCell>
                      <Typography style={{ fontWeight: 600, fontSize: "0.875rem" }}>{service.nome}</Typography>
                      {service.descricao && (
                        <Typography variant="caption" style={{ color: "#94a3b8" }} noWrap>
                          {service.descricao.substring(0, 70)}{service.descricao.length > 70 ? "…" : ""}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {service.tempoAtendimento ? (
                        <Chip
                          icon={<AccessTimeIcon style={{ fontSize: 14 }} />}
                          label={`${service.tempoAtendimento} min`}
                          size="small"
                          style={{ backgroundColor: "#e0f2fe", color: "#0369a1", fontWeight: 600, fontSize: "0.75rem" }}
                        />
                      ) : (
                        <span style={{ color: "#94a3b8" }}>—</span>
                      )}
                    </TableCell>
                    <TableCell align="right" style={{ fontWeight: 700 }}>
                      {formatCurrency(service.valorOriginal)}
                    </TableCell>
                    <TableCell align="center">
                      {isMobile ? (
                        <MobileActionMenu actions={[
                          { label: "Editar", icon: <EditIcon fontSize="small" />, onClick: () => handleOpenModal(service) },
                          { label: "Excluir", icon: <DeleteOutlineIcon fontSize="small" />, onClick: () => handleDelete(service), color: "#ef4444" },
                        ]} />
                      ) : (
                        <Box display="flex" justifyContent="center" style={{ gap: 4 }}>
                          <Tooltip title="Editar">
                            <IconButton size="small" style={{ color: "#1976d2" }} onClick={() => handleOpenModal(service)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" style={{ color: "#ef4444" }} onClick={() => handleDelete(service)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {filteredServices.length > 0 && (
            <Box className={classes.paginationBar}>
              <Typography variant="body2" style={{ color: "#666" }}>
                Exibindo {tablePage * rowsPerPage + 1} a {Math.min((tablePage + 1) * rowsPerPage, filteredServices.length)} de {filteredServices.length} serviço(s)
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Button size="small" disabled={tablePage === 0} onClick={() => setTablePage((p) => p - 1)} className={classes.actionBtn}>
                  Anterior
                </Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let idx = i;
                  if (totalPages > 5) {
                    const start = Math.max(0, Math.min(tablePage - 2, totalPages - 5));
                    idx = start + i;
                  }
                  return (
                    <Button
                      key={idx}
                      size="small"
                      variant={idx === tablePage ? "contained" : "text"}
                      color={idx === tablePage ? "primary" : "default"}
                      onClick={() => setTablePage(idx)}
                      style={{ minWidth: 32, borderRadius: 6, fontWeight: idx === tablePage ? 700 : 400 }}
                    >
                      {idx + 1}
                    </Button>
                  );
                })}
                <Button size="small" disabled={tablePage >= totalPages - 1} onClick={() => setTablePage((p) => p + 1)} className={classes.actionBtn}>
                  Próximo
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <ServiceModal
        open={modalOpen}
        onClose={handleCloseModal}
        service={selectedService}
        onSuccess={handleModalSuccess}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remover serviço</DialogTitle>
        <DialogContent>
          <Typography>Deseja remover o serviço <strong>{serviceToDelete?.nome}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button color="secondary" variant="contained" onClick={confirmDelete}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServicosPage;
